import React, { useState, useEffect, useMemo } from 'react';
import { logger } from '@/lib/logger';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAppStore, useAIToolsState } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { updateFixture } from '@/services/fixtureService';
import { useLVProducts } from '@/features/product-library/hooks/useLVProducts';
import { useAnnotations } from '@/hooks/useAnnotations';
import { useFixturesStandardized } from '@/hooks/entities/useFixturesStandardized';
import { useControlsStandardized } from '@/hooks/entities/useControlsStandardized';
import { useRoomsStandardized } from '@/hooks/entities/useRoomsStandardized';
import { useCableJunctionsStandardized } from '@/hooks/entities/useCableJunctionsStandardized';
import { useCablePathsStandardized } from '@/hooks/entities/useCablePathsStandardized';
import { deleteCablePath as deleteCablePathDirect } from '@/services/cablePathService';
import {
  deleteCableJunction as deleteCableJunctionDirect,
  getConnectedPathIds,
} from '@/services/cableJunctionService';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';

type DeleteTarget =
  | 'fixtures'
  | 'controls'
  | 'products'
  | 'annotations'
  | 'rooms'
  | 'cablePaths'
  | 'cableJunctions'
  | 'selected';

export function MultiSelectionDrawer() {
  const activeDrawer = useAppStore(state => state.ui.activeDrawer);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
  const selectedFixtures = useAppStore(state => state.selection.fixtures);
  const selectedControls = useAppStore(state => state.selection.controls);
  const selectedProducts = useAppStore(state => state.selection.products);
  const selectedAnnotations = useAppStore(state => state.selection.annotations);
  const selectedRooms = useAppStore(state => state.selection.rooms);
  const selectedCablePaths = useAppStore(state => state.selection.cablePaths);
  const selectedCableJunctions = useAppStore(state => state.selection.cableJunctions);
  const clearAllSelections = useAppStore(state => state.clearAllSelections);
  const setHoveredEntity = useAppStore(state => state.setHoveredEntity);

  const { data, floorPlan } = useFloorPlanContext();
  const floorPlanId = floorPlan.id;
  const [isUpdating, setIsUpdating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const aiTools = useAIToolsState();
  const queryClient = useQueryClient();

  // Get delete functions from hooks with optimistic updates
  const { deleteFixture: deleteFixtureOptimistic } = useFixturesStandardized(floorPlanId);
  const { deleteControl: deleteControlOptimistic } = useControlsStandardized(floorPlanId);
  const { deleteRoom: deleteRoomOptimistic } = useRoomsStandardized(floorPlanId);
  const { deleteProduct } = useLVProducts(floorPlanId);
  const { deleteAnnotation } = useAnnotations(floorPlanId);
  const {
    junctions,
    junctionCoordMap,
    refetch: refetchCableJunctions,
  } = useCableJunctionsStandardized(floorPlanId);
  const { paths: cablePaths, refetch: refetchCablePaths } = useCablePathsStandardized(
    floorPlanId,
    junctionCoordMap,
  );

  // Get toolbar state to check if we're in circuit mode
  const toolbarState = useAppStore(state => state.ui.toolbar);
  const isCircuitMode = toolbarState.activeMode === 'circuit';

  // Calculate total selections (excluding annotations - they use context toolbar)
  const nonCableSelectionsExcludingAnnotations =
    selectedFixtures.size + selectedControls.size + selectedProducts.size + selectedRooms.size;
  const selectedCablePathIds = Array.from(selectedCablePaths);
  const selectedCableJunctionIds = Array.from(selectedCableJunctions);
  const cableSelections = selectedCablePathIds.length + selectedCableJunctionIds.length;
  const totalSelectionsExcludingAnnotations =
    nonCableSelectionsExcludingAnnotations + cableSelections;
  const totalSelections = totalSelectionsExcludingAnnotations + selectedAnnotations.size;

  // Don't open drawer if only annotations are selected or in circuit mode
  const hasOnlyAnnotations =
    selectedAnnotations.size > 0 && totalSelectionsExcludingAnnotations === 0;
  const hasOnlyCables =
    cableSelections > 0 &&
    nonCableSelectionsExcludingAnnotations === 0 &&
    selectedAnnotations.size === 0;
  const isOpen =
    activeDrawer === 'selection' &&
    totalSelections > 1 &&
    !isCircuitMode &&
    !hasOnlyAnnotations &&
    !hasOnlyCables;

  // Get selected entities data - filter fixtures by confidence threshold
  const selectedFixtureData =
    data.fixtures?.filter(f => {
      if (!selectedFixtures.has(f.id)) return false;
      // Apply confidence threshold filter for visibility
      const confidence = f.confidence_score || 1;
      return confidence >= aiTools.confidenceThreshold;
    }) || [];
  const selectedControlData = data.controls?.filter(c => selectedControls.has(c.id)) || [];
  const selectedProductData = data.lvProducts?.filter(p => selectedProducts.has(p.id)) || [];
  // Use annotations from data context
  const selectedAnnotationData = data.annotations?.filter(a => selectedAnnotations.has(a.id)) || [];
  const selectedRoomData = data.rooms?.filter(r => selectedRooms.has(r.id)) || [];
  const selectedCablePathData = cablePaths?.filter(path => selectedCablePaths.has(path.id)) || [];
  const selectedCableJunctionData =
    junctions?.filter(junction => selectedCableJunctions.has(junction.id)) || [];

  // Get available circuits/loads
  const allCircuits = data.circuits || [];

  // Group fixtures by circuit
  const fixturesByCircuit = useMemo(() => {
    const grouped = new Map<string | null, typeof selectedFixtureData>();

    selectedFixtureData.forEach(fixture => {
      const circuitId = fixture.circuit_id || null;
      if (!grouped.has(circuitId)) {
        grouped.set(circuitId, []);
      }
      grouped.get(circuitId)!.push(fixture);
    });

    return grouped;
  }, [selectedFixtureData]);

  // Check if selection is only fixtures and if they all belong to the same circuit
  const circuitAnalysis = useMemo(() => {
    const isOnlyFixturesSelected =
      selectedFixtureData.length > 0 && selectedControlData.length === 0;
    const fixtureCircuitIds = new Set(selectedFixtureData.map(f => f.circuit_id).filter(Boolean));
    const allFixturesInSameCircuit = isOnlyFixturesSelected && fixtureCircuitIds.size === 1;
    const sharedCircuitId = allFixturesInSameCircuit ? Array.from(fixtureCircuitIds)[0] : null;

    // Filter out the shared circuit if all fixtures belong to it
    const availableCircuits = allFixturesInSameCircuit
      ? allCircuits.filter(c => c.id !== sharedCircuitId)
      : allCircuits;

    return {
      isOnlyFixturesSelected,
      allFixturesInSameCircuit,
      sharedCircuitId,
      availableCircuits,
    };
  }, [selectedFixtureData, selectedControlData, allCircuits]);

  const handleClose = () => {
    setActiveDrawer(null);
    clearAllSelections();
    setHoveredEntity(null, null); // Clear any lingering hover state
  };

  const refetchCableData = async () => {
    await Promise.allSettled([
      refetchCablePaths(),
      refetchCableJunctions(),
      queryClient.invalidateQueries({
        queryKey: queryKeys.entitiesByFloorPlan('cable_paths', floorPlanId),
        refetchType: 'all',
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.entitiesByFloorPlan('cable_junctions', floorPlanId),
        refetchType: 'all',
      }),
    ]);
  };

  const deleteCableItems = async ({
    pathIds,
    junctionIds,
  }: {
    pathIds: string[];
    junctionIds: string[];
  }) => {
    const pathIdsToDelete = new Set(pathIds);

    if (junctionIds.length > 0) {
      const connectedPathIdGroups = await Promise.all(
        junctionIds.map(junctionId => getConnectedPathIds(junctionId)),
      );
      connectedPathIdGroups.flat().forEach(pathId => pathIdsToDelete.add(pathId));
    }

    const dedupedPathIds = Array.from(pathIdsToDelete);
    const cablePathQueryKey = queryKeys.entitiesByFloorPlan('cable_paths', floorPlanId);
    const cableJunctionQueryKey = queryKeys.entitiesByFloorPlan('cable_junctions', floorPlanId);

    await Promise.allSettled([
      queryClient.cancelQueries({ queryKey: cablePathQueryKey }),
      queryClient.cancelQueries({ queryKey: cableJunctionQueryKey }),
    ]);

    const previousCablePaths = queryClient.getQueryData<any[]>(cablePathQueryKey);
    const previousCableJunctions = queryClient.getQueryData<any[]>(cableJunctionQueryKey);

    if (dedupedPathIds.length > 0) {
      queryClient.setQueryData<any[]>(cablePathQueryKey, (old = []) =>
        old.filter(path => !pathIdsToDelete.has(path.id)),
      );
    }

    if (junctionIds.length > 0) {
      const junctionIdsToDelete = new Set(junctionIds);
      queryClient.setQueryData<any[]>(cableJunctionQueryKey, (old = []) =>
        old.filter(junction => !junctionIdsToDelete.has(junction.id)),
      );
    }

    try {
      if (dedupedPathIds.length > 0) {
        await Promise.all(dedupedPathIds.map(pathId => deleteCablePathDirect(pathId)));
      }

      if (junctionIds.length > 0) {
        await Promise.all(junctionIds.map(junctionId => deleteCableJunctionDirect(junctionId)));
      }

      await refetchCableData();
    } catch (error) {
      queryClient.setQueryData(cablePathQueryKey, previousCablePaths);
      queryClient.setQueryData(cableJunctionQueryKey, previousCableJunctions);
      throw error;
    }
  };

  const handleAssignFixturesToCircuit = async (circuitId: string | null) => {
    if (selectedFixtureData.length === 0) return;

    try {
      setIsUpdating(true);

      // Update all selected fixtures
      await Promise.all(
        selectedFixtureData.map(fixture => updateFixture(fixture.id, { circuit_id: circuitId })),
      );

      await data.refetch();

      toast.success(
        circuitId
          ? `Assigned ${selectedFixtureData.length} fixtures to load`
          : `Unassigned ${selectedFixtureData.length} fixtures from load`,
      );

      handleClose();
    } catch (error) {
      logger.error('Error assigning fixtures:', error);
      toast.error('Failed to assign fixtures');
    } finally {
      setIsUpdating(false);
    }
  };

  // Category-specific delete functions
  const handleDeleteFixtures = async () => {
    if (selectedFixtureData.length === 0) return;

    try {
      setIsUpdating(true);

      // Clear fixture selections immediately
      selectedFixtureData.forEach(fixture => {
        useAppStore.getState().toggleEntitySelection('fixture', fixture.id);
      });

      // Delete all selected fixtures
      await Promise.all(
        selectedFixtureData.map(fixture =>
          deleteFixtureOptimistic(fixture.id, { showSuccessToast: false }).catch(err => {
            logger.error(`Failed to delete fixture ${fixture.id}:`, err);
            // Re-add to selection on error
            useAppStore.getState().toggleEntitySelection('fixture', fixture.id);
          }),
        ),
      );

      await data.refetch();
      toast.success(`Deleted ${selectedFixtureData.length} fixtures`);

      // Close drawer if no other selections remain
      if (
        selectedControlData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCablePathData.length === 0 &&
        selectedCableJunctionData.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting fixtures:', error);
      toast.error('Failed to delete fixtures');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteControls = async () => {
    if (selectedControlData.length === 0) return;

    try {
      setIsUpdating(true);

      // Clear control selections immediately
      selectedControlData.forEach(control => {
        useAppStore.getState().toggleEntitySelection('control', control.id);
      });

      // Delete all selected controls
      await Promise.all(
        selectedControlData.map(control =>
          deleteControlOptimistic(control.id, { showSuccessToast: false }).catch(err => {
            logger.error(`Failed to delete control ${control.id}:`, err);
            // Re-add to selection on error
            useAppStore.getState().toggleEntitySelection('control', control.id);
          }),
        ),
      );

      await data.refetch();
      toast.success(`Deleted ${selectedControlData.length} controls`);

      // Close drawer if no other selections remain
      if (
        selectedFixtureData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCablePathData.length === 0 &&
        selectedCableJunctionData.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting controls:', error);
      toast.error('Failed to delete controls');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProducts = async () => {
    if (selectedProductData.length === 0) return;

    try {
      setIsUpdating(true);

      // Clear product selections immediately
      selectedProductData.forEach(product => {
        useAppStore.getState().toggleEntitySelection('product', product.id);
      });

      // Delete all selected products
      await Promise.all(
        selectedProductData.map(product => deleteProduct(product.id, { showSuccessToast: false })),
      );

      await data.refetch();
      toast.success(`Deleted ${selectedProductData.length} products`);

      // Close drawer if no other selections remain
      if (
        selectedFixtureData.length === 0 &&
        selectedControlData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCablePathData.length === 0 &&
        selectedCableJunctionData.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting products:', error);
      toast.error('Failed to delete products');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteRooms = async () => {
    if (selectedRoomData.length === 0) return;

    try {
      setIsUpdating(true);

      // Clear room selections immediately
      selectedRoomData.forEach(room => {
        useAppStore.getState().toggleEntitySelection('room', room.id);
      });

      // Delete all selected rooms
      await Promise.all(
        selectedRoomData.map(room =>
          deleteRoomOptimistic(room.id, { showSuccessToast: false }).catch(err => {
            logger.error(`Failed to delete room ${room.id}:`, err);
            // Re-add to selection on error
            useAppStore.getState().toggleEntitySelection('room', room.id);
          }),
        ),
      );

      await data.refetch();
      toast.success(`Deleted ${selectedRoomData.length} rooms`);

      // Close drawer if no other selections remain
      if (
        selectedFixtureData.length === 0 &&
        selectedControlData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCablePathData.length === 0 &&
        selectedCableJunctionData.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting rooms:', error);
      toast.error('Failed to delete rooms');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAnnotations = async () => {
    if (selectedAnnotationData.length === 0) return;

    try {
      setIsUpdating(true);

      // Clear annotation selections immediately
      selectedAnnotationData.forEach(annotation => {
        useAppStore.getState().toggleEntitySelection('annotation', annotation.id);
      });

      const annotationIds = selectedAnnotationData.map(a => a.id);

      await Promise.all(
        annotationIds.map(id =>
          deleteAnnotation(id, { showSuccessToast: false }).catch(err => {
            logger.error(`Failed to delete annotation ${id}:`, err);
            // Re-add to selection on error
            useAppStore.getState().toggleEntitySelection('annotation', id);
          }),
        ),
      );

      await data.refetch();
      toast.success(`Deleted ${selectedAnnotationData.length} annotations`);

      // Close drawer if no other selections remain
      if (
        selectedFixtureData.length === 0 &&
        selectedControlData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedCablePathData.length === 0 &&
        selectedCableJunctionData.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting annotations:', error);
      toast.error('Failed to delete annotations');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCablePaths = async () => {
    if (selectedCablePathIds.length === 0) return;

    try {
      setIsUpdating(true);

      selectedCablePathIds.forEach(pathId => {
        useAppStore.getState().toggleEntitySelection('cablePath', pathId);
      });

      await deleteCableItems({ pathIds: selectedCablePathIds, junctionIds: [] });

      await data.refetch();
      toast.success(`Deleted ${selectedCablePathIds.length} cable paths`);

      if (
        selectedFixtureData.length === 0 &&
        selectedControlData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCableJunctionIds.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting cable paths:', error);
      selectedCablePathIds.forEach(pathId => {
        useAppStore.getState().toggleEntitySelection('cablePath', pathId);
      });
      toast.error('Failed to delete cable paths');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCableJunctions = async () => {
    if (selectedCableJunctionIds.length === 0) return;

    try {
      setIsUpdating(true);

      selectedCableJunctionIds.forEach(junctionId => {
        useAppStore.getState().toggleEntitySelection('cableJunction', junctionId);
      });

      await deleteCableItems({ pathIds: [], junctionIds: selectedCableJunctionIds });

      await data.refetch();
      toast.success(`Deleted ${selectedCableJunctionIds.length} cable junctions`);

      if (
        selectedFixtureData.length === 0 &&
        selectedControlData.length === 0 &&
        selectedProductData.length === 0 &&
        selectedRoomData.length === 0 &&
        selectedAnnotationData.length === 0 &&
        selectedCablePathIds.length === 0
      ) {
        handleClose();
      }
    } catch (error) {
      logger.error('Error deleting cable junctions:', error);
      selectedCableJunctionIds.forEach(junctionId => {
        useAppStore.getState().toggleEntitySelection('cableJunction', junctionId);
      });
      toast.error('Failed to delete cable junctions');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUnassignFromCircuit = async (circuitId: string) => {
    const fixturesInCircuit = fixturesByCircuit.get(circuitId) || [];
    if (fixturesInCircuit.length === 0) return;

    try {
      setIsUpdating(true);

      // Update fixtures in this circuit
      await Promise.all(
        fixturesInCircuit.map(fixture => updateFixture(fixture.id, { circuit_id: null })),
      );

      await data.refetch();

      toast.success(`Unassigned ${fixturesInCircuit.length} fixtures from load`);

      // Don't close drawer, just refresh the selection
      clearAllSelections();
    } catch (error) {
      logger.error('Error unassigning fixtures:', error);
      toast.error('Failed to unassign fixtures');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      setIsUpdating(true);

      // Snapshot cable selections directly from the store at click time. Rectangle selection
      // can update these Sets outside this render, so relying on render-time arrays can miss them.
      const selectionSnapshot = useAppStore.getState().selection;
      const deletingCablePathIds = Array.from(selectionSnapshot.cablePaths);
      const deletingCableJunctionIds = Array.from(selectionSnapshot.cableJunctions);

      logger.log('[MultiSelection] Deleting selected cable items:', {
        paths: deletingCablePathIds.length,
        junctions: deletingCableJunctionIds.length,
      });

      // Clear all selections immediately
      clearAllSelections();

      // Close drawer immediately for better UX
      handleClose();

      // Store promises for all deletions
      const deletionPromises = [];

      // Delete fixtures
      if (selectedFixtureData.length > 0) {
        deletionPromises.push(
          ...selectedFixtureData.map(fixture =>
            deleteFixtureOptimistic(fixture.id, { showSuccessToast: false }).catch(err => {
              logger.error(`Failed to delete fixture ${fixture.id}:`, err);
              // Re-add to selection on error
              useAppStore.getState().toggleEntitySelection('fixture', fixture.id);
            }),
          ),
        );
      }

      // Delete controls
      if (selectedControlData.length > 0) {
        deletionPromises.push(
          ...selectedControlData.map(control =>
            deleteControlOptimistic(control.id, { showSuccessToast: false }).catch(err => {
              logger.error(`Failed to delete control ${control.id}:`, err);
              // Re-add to selection on error
              useAppStore.getState().toggleEntitySelection('control', control.id);
            }),
          ),
        );
      }

      // Delete products (using hook with optimistic updates)
      if (selectedProductData.length > 0) {
        deletionPromises.push(
          ...selectedProductData.map(product =>
            deleteProduct(product.id, { showSuccessToast: false }),
          ),
        );
      }

      // Delete annotations (using hook with optimistic updates)
      if (selectedAnnotationData.length > 0) {
        const annotationIds = selectedAnnotationData.map(a => a.id);
        deletionPromises.push(
          ...annotationIds.map(id =>
            deleteAnnotation(id, { showSuccessToast: false }).catch(err => {
              logger.error(`Failed to delete annotation ${id}:`, err);
              // Re-add to selection on error
              useAppStore.getState().toggleEntitySelection('annotation', id);
            }),
          ),
        );
      }

      // Delete rooms
      if (selectedRoomData.length > 0) {
        deletionPromises.push(
          ...selectedRoomData.map(room =>
            deleteRoomOptimistic(room.id, { showSuccessToast: false }).catch(err => {
              logger.error(`Failed to delete room ${room.id}:`, err);
              // Re-add to selection on error
              useAppStore.getState().toggleEntitySelection('room', room.id);
            }),
          ),
        );
      }

      // Delete cable paths before junctions so connected selected junctions can be removed after.
      if (deletingCablePathIds.length > 0 || deletingCableJunctionIds.length > 0) {
        deletionPromises.push(
          deleteCableItems({
            pathIds: deletingCablePathIds,
            junctionIds: deletingCableJunctionIds,
          }).catch(err => {
            logger.error('Failed to delete selected cable items:', err);
            deletingCablePathIds.forEach(pathId => {
              useAppStore.getState().toggleEntitySelection('cablePath', pathId);
            });
            deletingCableJunctionIds.forEach(junctionId => {
              useAppStore.getState().toggleEntitySelection('cableJunction', junctionId);
            });
            throw err;
          }),
        );
      }

      // Wait for all deletions to complete and surface real failures.
      const deletionResults = await Promise.allSettled(deletionPromises);
      const failedDeletions = deletionResults.filter(result => result.status === 'rejected');
      if (failedDeletions.length > 0) {
        logger.error('One or more selected item deletions failed:', failedDeletions);
        throw new Error('Failed to delete some selected items');
      }

      // Force immediate refetch to ensure UI is in sync
      await data.refetch();

      const deletedCount =
        selectedFixtureData.length +
        selectedControlData.length +
        selectedProductData.length +
        selectedAnnotationData.length +
        selectedRoomData.length +
        deletingCablePathIds.length +
        deletingCableJunctionIds.length;
      toast.success(`Deleted ${deletedCount} items`);
    } catch (error) {
      logger.error('Error deleting items:', error);
      toast.error('Failed to delete some items');
      // Refetch to ensure UI is in sync with server state
      await data.refetch();
    } finally {
      setIsUpdating(false);
    }
  };

  const getDeleteTargetDetails = (target: DeleteTarget | null) => {
    switch (target) {
      case 'fixtures':
        return { count: selectedFixtureData.length, label: 'fixtures' };
      case 'controls':
        return { count: selectedControlData.length, label: 'controls' };
      case 'products':
        return { count: selectedProductData.length, label: 'products' };
      case 'annotations':
        return { count: selectedAnnotationData.length, label: 'annotations' };
      case 'rooms':
        return { count: selectedRoomData.length, label: 'rooms' };
      case 'cablePaths':
        return { count: selectedCablePathIds.length, label: 'cable paths' };
      case 'cableJunctions':
        return { count: selectedCableJunctionIds.length, label: 'cable junctions' };
      case 'selected':
        return { count: totalSelections, label: 'selected items' };
      default:
        return { count: 0, label: 'items' };
    }
  };

  const handleConfirmDeleteTarget = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);

    switch (target) {
      case 'fixtures':
        await handleDeleteFixtures();
        break;
      case 'controls':
        await handleDeleteControls();
        break;
      case 'products':
        await handleDeleteProducts();
        break;
      case 'annotations':
        await handleDeleteAnnotations();
        break;
      case 'rooms':
        await handleDeleteRooms();
        break;
      case 'cablePaths':
        await handleDeleteCablePaths();
        break;
      case 'cableJunctions':
        await handleDeleteCableJunctions();
        break;
      case 'selected':
        await handleDeleteSelected();
        break;
      default:
        break;
    }
  };

  // Auto-open drawer when multiple items are selected
  useEffect(() => {
    if (
      totalSelections > 1 &&
      !hasOnlyAnnotations &&
      !hasOnlyCables &&
      activeDrawer !== 'selection'
    ) {
      setActiveDrawer('selection');
    } else if (totalSelections <= 1 && activeDrawer === 'selection') {
      setActiveDrawer(null);
    }
  }, [totalSelections, hasOnlyAnnotations, hasOnlyCables, activeDrawer, setActiveDrawer]);

  if (!isOpen) return null;

  const deleteDetails = getDeleteTargetDetails(deleteTarget);

  return (
    <>
      <Sheet open={isOpen} modal={false}>
        <SheetContent
          className="w-[400px] sm:w-[540px] [&>button]:hidden flex flex-col h-full"
          onPointerDownOutside={e => e.preventDefault()}
          onInteractOutside={e => e.preventDefault()}
        >
          <SheetHeader className="flex-shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle>Multiple Selection</SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto mt-6 px-3">
            <div className="space-y-6 pb-6">
              {/* Selection Summary */}
              <div className="grid gap-2">
                {selectedFixtureData.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Fixtures</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedFixtureData.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('fixtures')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected fixtures"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedControlData.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Controls</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedControlData.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('controls')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected controls"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedProductData.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Products</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedProductData.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('products')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected products"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedAnnotationData.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Annotations</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedAnnotationData.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('annotations')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected annotations"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedRoomData.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Rooms</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedRoomData.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('rooms')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected rooms"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedCablePathIds.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Cable Paths</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedCablePathIds.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('cablePaths')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected cable paths"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
                {selectedCableJunctionIds.length > 0 && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Cable Junctions</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedCableJunctionIds.length}</Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget('cableJunctions')}
                        disabled={isUpdating}
                        className="h-7 w-7 p-0 focus:ring-1 focus:ring-offset-0"
                        title="Delete selected cable junctions"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Load Assignment (only for fixtures) */}
              {selectedFixtureData.length > 0 && (
                <>
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium">Load Assignment</h3>
                    <div className="space-y-3">
                      <Label>Assign Fixtures to Load</Label>
                      <Select
                        onValueChange={value => {
                          handleAssignFixturesToCircuit(value === 'none' ? null : value);
                        }}
                        disabled={isUpdating}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a load" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None (unassign)</SelectItem>

                          {circuitAnalysis.allFixturesInSameCircuit &&
                            circuitAnalysis.sharedCircuitId && (
                              <>
                                <Separator className="my-1" />
                                <div className="px-2 py-1.5 text-xs text-muted-foreground italic">
                                  All fixtures already assigned to same load
                                </div>
                              </>
                            )}

                          {circuitAnalysis.availableCircuits.length > 0 && (
                            <>
                              <Separator className="my-1" />
                              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                                {circuitAnalysis.allFixturesInSameCircuit
                                  ? 'Other Available Loads'
                                  : 'Available Loads'}
                              </div>
                              {circuitAnalysis.availableCircuits.map(circuit => (
                                <SelectItem
                                  key={circuit.id}
                                  value={circuit.id}
                                  onMouseEnter={() => setHoveredEntity(circuit.id, 'circuit')}
                                  onMouseLeave={() => setHoveredEntity(null, null)}
                                >
                                  {circuit.sequence_number ? `${circuit.sequence_number} - ` : ''}
                                  {circuit.name}
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Fixtures by Circuit section */}
              {selectedFixtureData.length > 0 &&
                Array.from(fixturesByCircuit.entries()).filter(([circuitId]) => circuitId !== null)
                  .length > 0 && (
                  <>
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium">Fixtures by Load</h3>
                      <div className="space-y-2">
                        {Array.from(fixturesByCircuit.entries())
                          .filter(([circuitId]) => circuitId !== null)
                          .map(([circuitId, fixtures]) => {
                            const circuit = allCircuits.find(c => c.id === circuitId);
                            if (!circuit) return null;

                            return (
                              <Card key={circuitId} className="p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="secondary">
                                      {circuit.sequence_number
                                        ? `${circuit.sequence_number} - `
                                        : ''}
                                      {circuit.name}
                                    </Badge>
                                    <span className="text-sm text-muted-foreground">
                                      {fixtures.length} fixture{fixtures.length > 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleUnassignFromCircuit(circuitId!)}
                                    disabled={isUpdating}
                                    className="h-7"
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Unassign
                                  </Button>
                                </div>
                              </Card>
                            );
                          })}
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

              {/* Actions */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium">Actions</h3>
                <div className="grid gap-2">
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteTarget('selected')}
                    disabled={isUpdating}
                    className="w-full"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {totalSelections} Items
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteDetails.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteDetails.count}{' '}
              {deleteDetails.count === 1
                ? deleteDetails.label.replace(/s$/, '')
                : deleteDetails.label}
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteTarget}
              disabled={isUpdating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
